/**
 * Theme provider wrapper that applies MUI theme based on user preference.
 * Supports light, dark, and system modes.
 * CoOrdio Cortex theme — CoOrdio Design System.
 */

// React Imports
import { useEffect, useState, useMemo } from 'react';

// MUI Imports
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { ConfirmDialogProvider } from '../components/common/ConfirmDialog';

// Hook Imports
import { useSettings } from '../@core/hooks/useSettings';

// Type Imports
import type { SystemMode } from '../@core/types';

// Theme Imports
import theme from './index';

// Type Imports
import type { ReactNode } from 'react';

type ThemeProviderProps = {
  children: ReactNode;
};

/**
 * Theme provider component
 */
export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const { settings } = useSettings();
  const [systemMode, setSystemMode] = useState<SystemMode>('dark');

  /**
   * Determine if dark mode should be active based on mode setting.
   */
  useEffect(() => {
    if (settings.mode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      setSystemMode(mediaQuery.matches ? 'dark' : 'light');

      const handleChange = (e: MediaQueryListEvent) => {
        setSystemMode(e.matches ? 'dark' : 'light');
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      setSystemMode(settings.mode === 'dark' ? 'dark' : 'light');
    }
  }, [settings.mode]);

  // Create theme
  const muiTheme = useMemo(() => {
    return theme(settings, systemMode, 'ltr');
  }, [settings, systemMode]);

  /**
   * Publish the active mode to the DOM under BOTH selectors.
   *
   * `data-theme` is what MUI switches on (`cssVariables.colorSchemeSelector` in ./index.ts).
   * The `dark` class is what the vendored CoOrdio token file switches on (`:root.dark` in
   * ./coordio/tokens.css). Writing only one of the two leaves the other system on its light
   * palette, so both are set together — see ./coordio/README.md.
   */
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', systemMode);
    root.classList.toggle('dark', systemMode === 'dark');
  }, [systemMode]);

  return (
    <MuiThemeProvider theme={muiTheme} defaultMode={systemMode}>
      <CssBaseline />
      <ConfirmDialogProvider>{children}</ConfirmDialogProvider>
    </MuiThemeProvider>
  );
};
